import { type ReactNode, createContext, useCallback, useEffect, useState } from 'react'

import dayjs from 'dayjs'
import toObject from 'dayjs/plugin/toObject'
import dayjsNullSupport from 'helpers/dayjsNullSupport'
import { type AlertProps } from 'shared/Alert'

export const SESSION_STORAGE_KEY = 'pendingAlert'

dayjs.extend(toObject)
dayjs.extend(dayjsNullSupport)

export interface AlertActionPayload extends AlertProps {
  id: string
  timestamp: any
}

interface AlertContextType {
  alerts: AlertProps[]
  addAlert: (alert: Omit<AlertActionPayload, 'id' | 'timestamp'>) => void
  removeAlert: (alert: AlertActionPayload) => void
}

export const AlertContext = createContext<AlertContextType | undefined>(undefined)

interface AlertProviderProps {
  children: ReactNode
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertProps[]>([])

  const addAlert = useCallback((alert: Omit<AlertActionPayload, 'id' | 'timestamp'>) => {
    const newAlert: AlertActionPayload = {
      closeOnTimeout: alert.severity === 'success',
      ...alert,
      id: Math.random().toString(36).substr(2, 10),
      timestamp: dayjs().toObject(),
    }
    setAlerts((prev) => [...prev, newAlert].slice(-4))
  }, [])

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (stored) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
      try {
        addAlert(JSON.parse(stored) as Omit<AlertActionPayload, 'id' | 'timestamp'>)
      } catch {
        // malformed stored value — ignore
      }
    }
  }, [addAlert])

  const removeAlert = useCallback((alert: AlertActionPayload) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alert.id))
  }, [])

  return <AlertContext.Provider value={{ alerts, addAlert, removeAlert }}>{children}</AlertContext.Provider>
}
